import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { t } from "@/i18n-js/instance";
import { ExistingAccountError } from "@circle-react/components/OnboardingPage/CreateProfilePage/ExistingAccountError";
import {
  EMAIL_REGEX,
  PASSWORD_REGEX,
} from "@circle-react/components/OnboardingPage/helpers/constants";
import { showRecaptcha } from "@circle-react/components/OnboardingPage/helpers/methods";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { useOnboardingTracker } from "@circle-react/components/OnboardingPage/hooks/useOnboardingTracker";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Spinner } from "@circle-react-shared/uikit/TipTap/VoiceMessage/components/Spinner";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import type { OnSubmitFunction } from "../../shared/uikit/Form/Form";
import { useIsNewOnboardingFlow } from "../hooks/useIsNewOnboardingFlow";
import { AcceptTermsLabel } from "./AcceptTermsLabel";
import EmailSuggestion from "./EmailSuggestion";
import type { SignUpFormProps } from "./types";
import { useEmailValidation } from "./useEmailValidation";

const localeScope = "authentication_app.signup";
const STEP_NUMBER = 0;

export const SignUpForm = ({
  currentCommunity,
  invitationToken,
  errors,
}: SignUpFormProps) => {
  const { search } = location;
  const params = new URLSearchParams(search);
  const prefillEmail = params.get("email");
  const couponCode = params.get("coupon_code");
  const queryParams = couponCode ? { coupon_code: couponCode } : {};
  const {
    isNewOnboardingFlow,
    onboardingButtonVariant,
    totalSteps,
    isLoading,
  } = useIsNewOnboardingFlow();

  const buttonText = couponCode
    ? t(`${localeScope}.form.buttons.promotion_submit`)
    : t(`${localeScope}.form.buttons.submit`);

  const shouldShowRecaptcha = showRecaptcha(window.RAILS_ENV);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { trackStepStarted, trackStepCompleted } = useOnboardingTracker({
    step_number: STEP_NUMBER,
    total_steps: totalSteps,
    step: "create_profile",
    fields: [
      {
        name: t(`${localeScope}.form.name`),
        type: "text",
      },
      {
        name: t(`${localeScope}.form.email`),
        type: "text",
      },
    ],
    config: {
      trackStartedAutomatically: false,
    },
  });

  const isSigninUpAsACommunityMember = Boolean(currentCommunity);
  const isCreatingACommunity = !isSigninUpAsACommunityMember;

  const shouldAlignTermsAndConditionsLeft =
    isNewOnboardingFlow && isCreatingACommunity;

  useEffect(() => {
    if (isCreatingACommunity && !isLoading) {
      trackStepStarted();
    }
  }, [trackStepStarted, currentCommunity, isLoading]);

  const defaultFormState = {
    user: {
      name: "",
      email: prefillEmail ?? "",
      password: "",
      community_id: currentCommunity?.id,
      invitation_token: invitationToken,
      terms_of_service: false,
    },
  };

  const { suggestedEmail, validateEmail } = useEmailValidation();

  const handleSubmit: OnSubmitFunction<typeof defaultFormState> = async (
    data,
    __,
    event,
  ) => {
    if (shouldShowRecaptcha) {
      return recaptchaRef.current?.executeAsync().then(() => {
        setIsSubmitting(true);
        if (isCreatingACommunity) {
          trackStepCompleted([data.user.name, data.user.email]);
        }
        event?.target?.submit();
      });
    }

    setIsSubmitting(true);
    if (isCreatingACommunity) {
      trackStepCompleted([data.user.name, data.user.email]);
    }
    event?.target?.submit();
  };

  useEffect(() => {
    if (errors) {
      setIsSubmitting(false);
    }
    if (errors?.email) {
      window.webview?.handleEmailAlreadyExistsError?.();
    }
  }, [errors]);

  return (
    <div className="flex w-full flex-col">
      <Form
        defaultValues={defaultFormState}
        action={`${authenticationAppRoutes.signUp({
          params: queryParams,
        })}#email`}
        method="POST"
        onSubmit={handleSubmit}
      >
        <div className="space-y-6">
          <Form.Item
            isFirstChild
            name="user[name]"
            label={t(`${localeScope}.form.name`)}
            hideDescription
            className="no-padding-top no-margin"
            hideBorder
            rules={{
              required: t(`${localeScope}.form.errors.name_required`),
            }}
          >
            <Form.Input autoFocus data-testid="user_name" />
          </Form.Item>
          <Form.Item
            name="user[email]"
            label={t(`${localeScope}.form.email`)}
            hideDescription
            className="no-padding-top no-margin"
            hideBorder
            rules={{
              required: t(`${localeScope}.form.errors.email_required`),
              pattern: {
                value: EMAIL_REGEX,
                message: t(`${localeScope}.form.errors.email_invalid`),
              },
              validate: { emailDeliverable: validateEmail },
            }}
          >
            <Form.Input type="email" data-testid="user_email" />
            {errors?.email && (
              <ExistingAccountError
                loginUrl={`${authenticationAppRoutes.signIn({
                  params: queryParams,
                })}#email`}
              />
            )}
          </Form.Item>
          {suggestedEmail && (
            <EmailSuggestion suggestedEmail={suggestedEmail} />
          )}
          <Form.Item
            name="user[password]"
            className="no-padding-top no-margin"
            label={t(`${localeScope}.form.password`)}
            hideDescription
            hideBorder
            rules={{
              required: t(`${localeScope}.form.errors.password_required`),
              pattern: {
                value: PASSWORD_REGEX,
                message: t(`${localeScope}.form.errors.password_invalid`),
              },
            }}
          >
            <Form.Input type="password" data-testid="password" />
          </Form.Item>

          <div
            className={classNames(
              "flex",
              shouldAlignTermsAndConditionsLeft
                ? "items-start justify-start"
                : "items-center justify-center",
            )}
          >
            <Form.Item
              id="user[terms_of_service]"
              name="user[terms_of_service]"
              hideDescription
              hideBorder
              className="!p-0"
              hideLabel
              rules={{
                required: t(`${localeScope}.form.errors.terms_required`),
              }}
            >
              <Form.Checkbox
                name="user[terms_of_service]"
                label={<AcceptTermsLabel />}
                data-testid="user_terms_of_service"
              />
            </Form.Item>
          </div>

          <div className="hidden">
            <Form.Item
              hiddenField
              name="user[affiliate_code]"
              hideDescription
              hideBorder
              hideLabel
            >
              <Form.Input type="hidden" />
            </Form.Item>

            <Form.Item
              hiddenField
              name="user[invitation_token]"
              hideDescription
              hideBorder
              hideLabel
            >
              <Form.Input type="hidden" />
            </Form.Item>

            <Form.Item
              hiddenField
              name="user[community_id]"
              hideDescription
              hideBorder
              hideLabel
            >
              <Form.Input type="hidden" />
            </Form.Item>

            <Form.Item
              name="user[affiliate_ref]"
              hiddenField
              hideDescription
              hideBorder
              hideLabel
            >
              <Form.Input type="hidden" />
            </Form.Item>
          </div>
          {shouldShowRecaptcha && window.CAPTCHA_SITE_KEY && (
            <div className="flex w-full items-center justify-center">
              <ReCAPTCHA
                sitekey={window.CAPTCHA_SITE_KEY}
                size="invisible"
                ref={recaptchaRef}
              />
            </div>
          )}
          <div className="mt-6 w-full text-center">
            <div className="mx-auto flex w-full">
              <Button
                variant={currentCommunity ? "primary" : onboardingButtonVariant}
                type="submit"
                className="disabled:bg-brand disabled:text-brand-button"
                full
                extraLarge
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <Spinner />
                  </div>
                ) : (
                  buttonText
                )}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};
